import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Rocket, CheckCheck, RefreshCw, Clock, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

// v78.42 — Bumped 30 s → 90 s and added visibility-pause to drop background
// network pressure on mid-range Android. New notifications still surface
// within 90 s (acceptable for a non-realtime channel — true real-time
// delivery already happens via the chat WebSocket). The previous 30-s
// cadence was, together with the home-feed 45-s poll, the second-biggest
// contributor to perceived UI sluggishness reported by founder.
const POLL_MS = 90000;

function timeAgo(iso) {
    if (!iso) return "";
    const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    if (s < 86400) return `${Math.round(s / 3600)}h`;
    return `${Math.round(s / 86400)}d`;
}

/**
 * NotificationsBell — bell icon in the header with a dropdown of recent
 * alerts. Pull-to-refresh + 90 s background poll with visibility-pause.
 *
 * @param {number} sideOffset — distance in px from trigger-bottom to
 *   dropdown-panel-top. Caller must pass this so the panel opens BELOW
 *   the sticky header instead of overlapping the search bar area. See
 *   v78.48 fix in Header.jsx for the calculated values per viewport.
 */
export default function NotificationsBell({ sideOffset = 8 }) {
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/notifications");
            setItems(data.items || []);
            setUnread(data.unread || 0);
        } catch { /* silent */ }
    }, []);

    useEffect(() => {
        load();
        // v78.42 — Pause polling when tab is hidden — saves background
        // network traffic when the user has the tab open in a back stack.
        // Also refetch eagerly the moment the tab becomes visible again so
        // a returning user sees fresh notifications without waiting for
        // the next interval tick.
        const tick = () => {
            if (document.visibilityState === "visible") load();
        };
        const t = setInterval(tick, POLL_MS);
        const onVis = () => { if (document.visibilityState === "visible") load(); };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            clearInterval(t);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [load]);

    const openItem = async (n) => {
        setOpen(false);
        if (!n.read) {
            try { await api.post(`/notifications/${n.id}/read`); } catch { /* noop */ }
        }
        navigate(n.link || "/alerts");
        load();
    };

    const markAll = async (e) => {
        e.stopPropagation();
        try { await api.post("/notifications/mark-all-read"); } catch { /* noop */ }
        load();
    };

    // v78.30 — Per-row dismiss. Soft-archives the alert so it never
    // surfaces again in the bell or /alerts page.
    const dismiss = async (e, n) => {
        e.stopPropagation();
        e.preventDefault();
        if (busyId === n.id) return;
        setBusyId(n.id);
        // Optimistic UI — remove immediately for snappy feedback.
        setItems((prev) => prev.filter((x) => x.id !== n.id));
        if (!n.read) setUnread((u) => Math.max(0, u - 1));
        try { await api.post(`/notifications/${n.id}/dismiss`); }
        catch (err) { toast.error(formatApiError(err)); load(); }
        finally { setBusyId(null); }
    };

    const clearRead = async (e) => {
        e.stopPropagation();
        try {
            const { data } = await api.post("/notifications/clear-read");
            if (data?.dismissed > 0) toast.success(`Cleared ${data.dismissed} read alert${data.dismissed > 1 ? "s" : ""}`);
            else toast.message("Nothing to clear");
        } catch (err) { toast.error(formatApiError(err)); }
        load();
    };

    const republish = async (e, n) => {
        e.stopPropagation();
        if (!n.listing_id) return;
        try {
            await api.post(`/listings/${n.listing_id}/republish`);
            toast.success("Republished for 30 more days");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const iconFor = (type) => {
        if (type === "listing_expired") return <RefreshCw className="w-4 h-4" />;
        if (type === "listing_expiring") return <Clock className="w-4 h-4" />;
        // v79.167 — Funboard events emit unicode emojis; render them
        // instead of a generic icon so the bell dropdown feels social.
        if (type === "funboard_reaction") return <span className="text-base leading-none">❤️</span>;
        if (type === "funboard_subscribe") return <span className="text-base leading-none">🎉</span>;
        return <Rocket className="w-4 h-4" />;
    };

    const preview = items.slice(0, 5);
    const readCount = items.filter((n) => n.read).length;

    return (
        <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
            <DropdownMenuTrigger asChild>
                <button
                    data-testid="notifications-bell"
                    className="relative h-10 w-10 rounded-full bg-aduku-navy-800/70 hover:bg-aduku-navy-700 text-white/90 hover:text-white flex items-center justify-center transition-colors"
                    aria-label="Alerts"
                >
                    <Bell className="w-5 h-5" />
                    {unread > 0 && (
                        <span
                            data-testid="notifications-unread-count"
                            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-aduku-yellow text-aduku-navy text-[10px] font-bold flex items-center justify-center"
                        >
                            {unread > 9 ? "9+" : unread}
                        </span>
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={sideOffset} className="w-80 p-0">
                <div className="flex items-center justify-between px-3 py-2 border-b border-aduku-border">
                    <div className="font-display font-bold text-aduku-navy">Alerts</div>
                    <div className="flex items-center gap-2">
                        {unread > 0 && (
                            <button
                                data-testid="bell-mark-all-read"
                                onClick={markAll}
                                className="text-[11px] font-semibold text-aduku-navy/70 hover:text-aduku-navy inline-flex items-center gap-1"
                            >
                                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                            </button>
                        )}
                        {readCount > 0 && (
                            <button
                                data-testid="bell-clear-read"
                                onClick={clearRead}
                                className="text-[11px] font-semibold text-aduku-navy/70 hover:text-aduku-navy inline-flex items-center gap-1"
                                title="Remove all already-read alerts"
                            >
                                <X className="w-3.5 h-3.5" /> Clear read
                            </button>
                        )}
                    </div>
                </div>
                {preview.length === 0 ? (
                    <div className="p-6 text-center text-aduku-navy/60 text-sm">No alerts yet</div>
                ) : (
                    <ul className="max-h-80 overflow-auto">
                        {preview.map((n) => {
                            const isExpiry = n.type === "listing_expired" || n.type === "listing_expiring";
                            const isUnread = !n.read;
                            return (
                                <li
                                    key={n.id}
                                    onClick={() => openItem(n)}
                                    data-testid={`bell-alert-${n.id}`}
                                    data-state={isUnread ? "unread" : "read"}
                                    className={`group relative px-3 py-2.5 border-b border-aduku-border last:border-b-0 cursor-pointer flex items-start gap-2.5 transition-colors ${
                                        isUnread
                                            ? "bg-aduku-yellow/10 hover:bg-aduku-yellow/15 border-l-[3px] border-l-aduku-yellow"
                                            : "bg-white hover:bg-aduku-bg opacity-75 hover:opacity-100"
                                    }`}
                                >
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                                        isUnread ? "bg-aduku-yellow/30 text-aduku-navy" : "bg-aduku-bg text-aduku-navy/60"
                                    }`}>
                                        {isUnread ? iconFor(n.type) : <Check className="w-4 h-4" />}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className={`text-sm truncate ${isUnread ? "font-bold text-aduku-navy" : "font-medium text-aduku-navy/70"}`}>
                                                {n.title}
                                            </div>
                                            <div className="text-[10px] text-aduku-navy/50 shrink-0">{timeAgo(n.scheduled_for || n.created_at)}</div>
                                        </div>
                                        <p className={`text-xs line-clamp-2 ${isUnread ? "text-aduku-navy/80" : "text-aduku-navy/55"}`}>{n.body}</p>
                                        <div className="flex items-center gap-2 mt-1">
                                            {isUnread ? (
                                                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-aduku-yellow text-aduku-navy">New</span>
                                            ) : (
                                                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-aduku-bg text-aduku-navy/55 border border-aduku-border">Read</span>
                                            )}
                                            {isExpiry && n.listing_id && (
                                                <button
                                                    data-testid={`bell-republish-${n.listing_id}`}
                                                    onClick={(e) => republish(e, n)}
                                                    className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-aduku-navy text-aduku-yellow hover:bg-aduku-navy-700 text-[10px] font-bold"
                                                >
                                                    <RefreshCw className="w-3 h-3" /> Republish 30d
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {/* Always-visible dismiss button — most explicit way to "mark as done". */}
                                    <button
                                        data-testid={`bell-dismiss-${n.id}`}
                                        onClick={(e) => dismiss(e, n)}
                                        aria-label="Dismiss alert"
                                        title="Dismiss"
                                        className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-aduku-navy/40 hover:text-white hover:bg-aduku-navy transition-colors"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
                <button
                    data-testid="bell-see-all"
                    onClick={() => { setOpen(false); navigate("/alerts"); }}
                    className="w-full px-3 py-2.5 border-t border-aduku-border text-sm font-semibold text-aduku-navy hover:bg-aduku-bg"
                >
                    See all alerts
                </button>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
